"use strict"

import React, { useCallback, useContext, useEffect, useState, useMemo } from "react"
import { useIntl } from "react-intl"

import { logToServer } from "../ErrorBoundary"
import { getObjectsList } from "../Loaders"

import { FormattedMessage, IntlProvider } from "react-intl"
import ToggleLanguageButton from "../components/internationalization/ToggleLanguageButton"
import OperatorActionWrapper from "../components/operatoraction/OperatorActionWrapper"
import en from "../translations/en.json"
import zh from "../translations/zh.json"
import ja from "../translations/ja.json"
import nl from "../translations/nl.json"
import LogoutTimer from "../shared_components/widgets/LogoutTimer"
import UserRequestModal from "../components/widgets/UserRequestModal"
import ConfidentialityModal from "../components/widgets/ConfidentialityModal"
import { API_STATUS } from "../shared_components/widgets/utils"

const messages = {
  en: en,
  zh: zh,
  ja: ja,
  nl: nl,
}

export const AppSettingConfigContext = React.createContext({})

export function withAppSettingsConfig(Component) {
  return function (props) {
    return (
      <AppSettingConfigContext.Consumer>
        {(configData) => <Component {...props} {...configData} />}
      </AppSettingConfigContext.Consumer>
    )
  }
}

export default function AppSettingsConfigProvider(props) {
  const [config, setConfig] = useState({
    user: {},
    browser_language: navigator.language.split(/[-_]/)[0],
    locales_supported: [],
    locale: "", //default locale for translations
  })
  const [defaultReactor, setDefaultReactorState] = useState("")

  // Keep track of which runs have had new actions added (so we can trigger an action reload)
  const [newActionRuns, setNewActionRuns] = useState([])

  const setDefaultReactor = useCallback((reactor) => {
    setDefaultReactorState(reactor)
  }, [])

  const setPreferredLanguage = useCallback((language) => {
    setConfig((prevState) => {
      return {
        ...prevState,
        locale: language,
      }
    })
  }, [])

  const [configErr, setConfigErr] = useState(false)
  const reloadConfig = useCallback(() => {
    getObjectsList("/backend/jsconfig/")
      .then((config) => {
        let locale
        const browser_language = navigator.language.split(/[-_]/)[0]
        if (config.user && config.user.language_preference) {
          locale = config.user.language_preference
        } else if (browser_language && config.locales_supported.includes(browser_language)) {
          locale = browser_language
        } else {
          locale = config.default_locale
        }
        window.lanzacofee.language = locale
        setConfig((prevState) => {
          return {
            ...prevState,
            ...config,
            locale: locale,
          }
        })
        setConfigErr(false)
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        setConfigErr(true)
      })
  }, [])

  const [activeUsers, setActiveUsers] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [userStatus, setUserStatus] = useState(API_STATUS.LOADING)
  const [userErr, setUserErr] = useState(false)
  const reloadUsers = useCallback(() => {
    setUserStatus(API_STATUS.LOADING)
    getObjectsList("/users/list/")
      .then((users) => {
        setUserStatus(API_STATUS.SUCCESS)
        setAllUsers(users)
        setActiveUsers(users.filter((u) => u.is_active && !u.is_system_user))
        setUserErr(false)
      })
      .catch((err) => {
        setUserStatus(API_STATUS.ERROR)
        console.error(err)
        logToServer(err)
        setUserErr(true)
      })
  }, [])

  useEffect(() => {
    reloadConfig()
    reloadUsers()
  }, [])

  const value = useMemo(
    () => ({
      config: config,
      setPreferredLanguage: setPreferredLanguage,
      reloadConfig: reloadConfig,
      configErr: configErr,
      setDefaultReactor: setDefaultReactor,
      defaultReactor: defaultReactor,
      newActionRuns: newActionRuns,
      setNewActionRuns: setNewActionRuns,
      activeUsers: activeUsers,
      allUsers: allUsers,
      reloadUsers: reloadUsers,
      userErr: userErr,
      userStatus: userStatus,
    }),
    [config, defaultReactor, newActionRuns, activeUsers, allUsers, userStatus],
  )

  return <AppSettingConfigContext.Provider value={value}>{props.children}</AppSettingConfigContext.Provider>
}

export function PageWrapper({ children }) {
  const config = useContext(AppSettingConfigContext)
  const [locale, setLocale] = useState("en")
  const [username, setUsername] = useState("")

  const shouldShowForm = config?.config?.settings?.SHOW_USER_REQUEST_FORM

  useEffect(() => {
    setUsername(config.config.user.username)
  }, [config.config])

  useEffect(() => {
    window.lanzacofee.language ? setLocale(window.lanzacofee.language) : null
  }, [config.config, window.lanzacofee.language])

  return (
    <IntlProvider locale={locale} messages={messages[locale]}>
      <IntlPageWrapper children={children} />
      <div style={{ display: "flex" }} className={"top-menu-items"}>
        {username && (
          <p style={{ marginTop: "4px" }}>
            <FormattedMessage
              id={"general.currently.logged.in.as"}
              description={"Currently Logged In As Label"}
            />
            : &nbsp;
            <b>{username}</b>
          </p>
        )}
        &nbsp;&nbsp;
        <ToggleLanguageButton />
        {shouldShowForm && <UserRequestModal operator={username} />}
        <ConfidentialityModal config={config?.config || {}} reloadConfig={config.reloadConfig} />
      </div>
      <LogoutTimer />
      <OperatorActionWrapper />
    </IntlProvider>
  )
}

function IntlPageWrapper({ children }) {
  const renderChildren = () => {
    return React.Children.map(children, (child) => {
      return React.cloneElement(child, {
        intl: useIntl(),
      })
    })
  }

  return <>{renderChildren()}</>
}

export function PageTitle({ id = "", description = "", title = <></>, defaultMessage, glyph, ExtraElement }) {
  let translationProps = {
    id: `${id}`,
    description: `${description} Page Title`,
  }
  if (defaultMessage) {
    translationProps = { ...translationProps, defaultMessage: defaultMessage }
  }
  return (
    <div className="row" style={{ marginBottom: "15px" }}>
      <div className={"col-8"}>
        <h4 style={{ marginTop: 0 }}>
          <span className={`fa fa-${glyph}`} style={{ marginRight: "20px" }} />
          {id && <FormattedMessage {...translationProps} />}
          {title && <>{title}</>}
        </h4>
      </div>
      <div className={"col-4 text-end"}>{ExtraElement && <ExtraElement />}</div>
    </div>
  )
}
